/**
 * Section: Tw card list
 * -----------------------------------------------------------------------------
 * Keeps the carousel pagination in sync with the scroll-snap list and scrolls
 * to the corresponding card when a dot is clicked. Shows one dot per position
 * the list can actually reach, the last one being the end of the list.
 *
 * Scrolling itself is handled by CSS.
 */

if (!customElements.get('tw-card-list-slider')) {

    /**
     * Custom element wrapping the card list and pagination.
     */
    class TwCardListSlider extends HTMLElement {
  
      /**
       * Cache elements and bind listeners.
       */
      connectedCallback() {
        this.list = this.querySelector('[js-card-list="list"]')
        this.cards = [...this.querySelectorAll('[js-card-list="card"]')]
        this.dots = [...this.querySelectorAll('[js-card-list="dot"]')]
  
        if (!this.list || !this.dots.length) {
          return
        }
  
        this.carouselQuery = window.matchMedia('(max-width: 959px)')
        this.handleClick = this.handleClick.bind(this)
        this.handleQueryChange = this.handleQueryChange.bind(this)
        this.update = this.update.bind(this)
  
        this.addEventListener('click', this.handleClick)
        this.list.addEventListener('scroll', this.update, { passive: true })
        this.list.addEventListener('scrollend', this.update, { passive: true })
        this.carouselQuery.addEventListener('change', this.handleQueryChange)
        // The reachable positions change with the list width
        this.resizeObserver = new ResizeObserver(this.update)
        this.resizeObserver.observe(this.list)
  
        this.update()
      }
  
      /**
       * Remove listeners when the section is removed in the theme editor.
       */
      disconnectedCallback() {
        if (!this.list || !this.dots.length) {
          return
        }
  
        this.removeEventListener('click', this.handleClick)
        this.list.removeEventListener('scroll', this.update)
        this.list.removeEventListener('scrollend', this.update)
        this.carouselQuery.removeEventListener('change', this.handleQueryChange)
        this.resizeObserver.disconnect()
      }
  
      /**
       * Scroll offset of a card, measured from the first card, which sits
       * at scroll 0.
       *
       * @param {HTMLElement} card
       * @returns {number}
       */
      offsetOf(card) {
        return card.getBoundingClientRect().left -
          this.cards[0].getBoundingClientRect().left
      }

      /**
       * Number of positions the list can scroll to. Cards near the end can't
       * reach the start of the list once it's scrolled all the way, so they
       * share the last position, which is the end of the list.
       *
       * @returns {number}
       */
      positionCount() {
        const maxScroll = this.list.scrollWidth - this.list.clientWidth

        if (maxScroll <= 1) {
          return 1
        }

        const offsets = this.cards.map((card) => this.offsetOf(card))
        const reachable = offsets.filter((offset) => offset <= maxScroll + 2).length
        const endsOnCard = offsets[reachable - 1] >= maxScroll - 2

        return Math.min(endsOnCard ? reachable : reachable + 1, this.cards.length)
      }

      /**
       * Scroll to the position of the clicked pagination dot.
       *
       * @param {MouseEvent} event
       */
      handleClick(event) {
        const dot = event.target.closest('[js-card-list="dot"]')

        if (!dot || !this.carouselQuery.matches) {
          return
        }

        const index = this.dots.indexOf(dot)
        const count = this.positionCount()
        const maxScroll = this.list.scrollWidth - this.list.clientWidth

        if (index < 0 || index >= count) {
          return
        }

        // The last dot always goes to the very end of the list
        const left = index === count - 1 ? maxScroll : this.offsetOf(this.cards[index])

        this.list.scrollTo({
          behavior: 'instant',
          left,
        })

        this.update()
      }

      /**
       * Reset the horizontal scroll position when leaving carousel mode.
       */
      handleQueryChange() {
        if (!this.carouselQuery.matches) {
          this.list.scrollLeft = 0
        }
  
        this.update()
      }
  
      /**
       * Show one dot per reachable position and mark the current one: the end
       * of the list, or the card nearest the start of the scrollable area.
       */
      update() {
        if (!this.carouselQuery.matches) {
          return
        }

        const count = this.positionCount()
        const maxScroll = this.list.scrollWidth - this.list.clientWidth
        let current = 0

        if (this.list.scrollLeft >= maxScroll - 2) {
          current = count - 1
        } else {
          let nearestDistance = Infinity

          this.cards.slice(0, count).forEach((card, index) => {
            const distance = Math.abs(this.offsetOf(card) - this.list.scrollLeft)

            if (distance < nearestDistance) {
              nearestDistance = distance
              current = index
            }
          })
        }

        this.dots.forEach((dot, index) => {
          dot.hidden = index >= count
          dot.setAttribute('aria-current', String(index === current))
        })
      }
    }

    customElements.define('tw-card-list-slider', TwCardListSlider)
  }